import { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../i18n';
import StepSlides from '../slides/StepSlides';

const ArrowSvg = () => (
  <svg className="chip-arr" width="22" height="12" viewBox="0 0 22 12" fill="none">
    <line x1="0" y1="6" x2="15" y2="6" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M13 2L19 6L13 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

/** Split a long text string into bullet items at ·, ;, or newlines */
function toBullets(text: string): string[] {
  return text
    .split(/[·;\n]+/)
    .map(s => s.trim())
    .filter(Boolean);
}

function OverviewModal({ onClose }: { onClose: () => void }) {
  const { dict } = useTranslation();
  const th = dict.lc_steps_th;
  const tableData = dict.lc_steps_data;

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box" role="dialog" aria-modal="true" aria-label="Steps Overview">
        <div className="modal-head">
          <span className="modal-head-title">📋 {dict.lc_steps_label}</span>
          <button className="modal-close" onClick={onClose}>{dict.step_panel_close}</button>
        </div>
        <div className="modal-body">
          <div className="steps-table-wrap">
            <table className="steps-table">
              <thead>
                <tr>{th.map((h, i) => <th key={i}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {tableData.map((row, i) => {
                  const bullets = toBullets(row[3] ?? '');
                  return (
                    <tr key={i}>
                      <td>{row[0]}</td>
                      <td>{row[1]}</td>
                      <td>{row[2]}</td>
                      <td>
                        {bullets.length > 1 ? (
                          <ul className="td-bullets">
                            {bullets.map((b, j) => <li key={j}>{b}</li>)}
                          </ul>
                        ) : row[3]}
                      </td>
                      <td>{row[4]}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LayerC() {
  const { dict } = useTranslation();
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [showOverview, setShowOverview] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const steps = dict.lc_steps;

  useEffect(() => {
    if (activeStep !== null && panelRef.current) {
      setTimeout(() => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
    }
  }, [activeStep]);

  function toggle(i: number) {
    setActiveStep(prev => (prev === i ? null : i));
  }

  return (
    <div className="layer layer-c">
      {showOverview && <OverviewModal onClose={() => setShowOverview(false)} />}

      <div className="layer-wm-wrap">
        <img
          className="layer-wm"
          src="https://ntq.com.vn/wp-content/themes/ntq_branding/assets/images/NTQ-logo.png"
          alt="" aria-hidden="true"
          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
      </div>

      <div className="layer-header">
        <span className="layer-badge badge-c">{dict.lc_badge}</span>
        <div style={{ flex: 1 }}>
          <div className="layer-title">{dict.lc_title}</div>
          <div className="layer-sub">{dict.lc_sub}</div>
        </div>
        <button className="overview-btn" onClick={() => setShowOverview(true)}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <rect x="1" y="1" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            <line x1="4" y1="5" x2="10" y2="5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            <line x1="4" y1="7.5" x2="10" y2="7.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            <line x1="4" y1="10" x2="8" y2="10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
          Overview
        </button>
      </div>

      <div className="layer-body">
        <div className="sec-label">{dict.lc_steps_label}</div>

        {/* Steps grid — split into 2 rows of 5, inline panel between/below */}
        {[0, 5].map(rowStart => {
          const rowStepsData = steps.slice(rowStart, rowStart + 5);
          const rowContainsActive =
            activeStep !== null && activeStep >= rowStart && activeStep < rowStart + 5;
          return (
            <div key={rowStart}>
              <div className="s-grid">
                {rowStepsData.map((name, i) => {
                  const idx = rowStart + i;
                  const active = activeStep === idx;
                  return (
                    <button
                      key={idx}
                      className={`s-box${active ? ' active' : ''}`}
                      onClick={() => toggle(idx)}
                      aria-expanded={active}
                    >
                      <span className="s-num">{idx + 1}</span>
                      <span className="s-name">{name}</span>
                    </button>
                  );
                })}
              </div>
              {rowContainsActive && activeStep !== null && (
                <div
                  ref={panelRef}
                  className="step-panel"
                  role="region"
                  aria-label={`Step ${activeStep + 1} detail`}
                >
                  <div className="step-panel-head">
                    <div className="step-panel-badge">S{activeStep + 1}</div>
                    <div className="step-panel-head-text">
                      <div className="step-panel-title">{steps[activeStep]}</div>
                    </div>
                    <div className="step-panel-actions">
                      <button
                        className="step-panel-btn"
                        disabled={activeStep === 0}
                        onClick={() => setActiveStep(v => v !== null && v > 0 ? v - 1 : v)}
                      >
                        {dict.step_panel_prev}
                      </button>
                      <button
                        className="step-panel-btn"
                        disabled={activeStep === steps.length - 1}
                        onClick={() => setActiveStep(v => v !== null && v < steps.length - 1 ? v + 1 : v)}
                      >
                        {dict.step_panel_next}
                      </button>
                      <button className="step-panel-btn step-panel-btn--close" onClick={() => setActiveStep(null)}>
                        {dict.step_panel_close}
                      </button>
                    </div>
                  </div>
                  <div className="step-panel-body">
                    <StepSlides stepIndex={activeStep} deck={dict.deck} />
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Hint when nothing open */}
        {activeStep === null && (
          <div className="step-panel-hint">{dict.step_panel_open_hint}</div>
        )}

        {/* Flow */}
        <div className="sec-label mt16">{dict.lc_flow_label}</div>
        <div className="flow-row">
          <span className="flow-chip fcc">{dict.lc_f1}</span>
          <ArrowSvg />
          <span className="flow-chip fcc">{dict.lc_f2}</span>
          <ArrowSvg />
          <span className="flow-chip fcc">{dict.lc_f3}</span>
          <ArrowSvg />
          <span className="flow-chip fcc">{dict.lc_f4}</span>
        </div>
        <div className="flow-row mt6">
          <span className="flow-chip fcc">{dict.lc_f5}</span>
          <ArrowSvg />
          <span className="flow-chip fcc">{dict.lc_f6}</span>
          <ArrowSvg />
          <span className="flow-chip fcc">{dict.lc_f7}</span>
        </div>

        <div className="sub2 mt10">
          <div className="sub-card">
            <div className="sub-card-t"><span className="dot dc" /><span>{dict.lc_m1_t}</span></div>
            <div className="sub-card-d">{dict.lc_m1_d}</div>
          </div>
          <div className="sub-card">
            <div className="sub-card-t"><span className="dot dc" /><span>{dict.lc_m2_t}</span></div>
            <div className="sub-card-d">{dict.lc_m2_d}</div>
          </div>
        </div>

        <div className="contrib mt10">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: '1px' }}>
            <path d="M8 13V3M4 7l4-4 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>{dict.lc_contrib}</span>
        </div>
      </div>
    </div>
  );
}
